import { useEffect, useState } from "react";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  IconButton,
  Icon,
  Typography,
  Modal,
  Paper,
  Button,
  Grid,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { RetornoDaListagemVazia } from "../../shared/components/retornoDaListagem/retornoDaListagem";
import { contasAPagarService } from "./service/criar-contas-a-pagar.service";
import { toast } from "react-toastify";
import { toastError } from "../../shared/services/errors/erros-handler";
import { useLoader } from "../../shared/contexts/LoaderContext";

interface Parcela {
  id: string;
  description: string;
  value: string | number;
  status: string;
  dueDate: string;
  paymentDate?: string | null;
}

interface ContaAPagar {
  id: string;
  description: string;
  totalValue: string | number;
  status: string;
  anticipateAccount: boolean;
  paidInstallments: number;
  openInstallments: number;
  installments?: Parcela[];
}

type ResumoItem = { sum: number; qty: number };
export type ResumoMes = {
  pending: ResumoItem;
  paid: ResumoItem;
  anticipated?: ResumoItem;
  canceled?: ResumoItem;
};

// resposta pode vir com chaves open/closed
type SumResponse = Partial<{
  pending: ResumoItem;
  paid: ResumoItem;
  open: ResumoItem;
  closed: ResumoItem;
  anticipated: ResumoItem;
  canceled: ResumoItem;
}>;

const statusOptions = [
  { value: "open", label: "Aberta" },
  { value: "paid", label: "Paga" },
  { value: "anticipated", label: "Antecipada" },
  { value: "canceled", label: "Cancelada" },
];

function ModalFiltrosContasAPagar({
  open,
  onClose,
  onFiltrar,
}: {
  open: boolean;
  onClose: () => void;
  onFiltrar: (filters: any) => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));


  // Último dia do mês atual
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const dateInicio = new Date(
    firstDay.getTime() - firstDay.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 10);
  const today = new Date(
    lastDay.getTime() - lastDay.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 10);

  const [dateStart, setDateStart] = useState(dateInicio);
  const [dateEnd, setDateEnd] = useState(today);
  const [status, setStatus] = useState<string[]>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFiltrar({ dateStart, dateEnd, status });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          width: { xs: "92%", sm: 420 },
          maxWidth: "96vw",
          margin: "80px auto",
          padding: 3,
          outline: "none",
        }}
      >
        <Typography variant="h6" mb={2}>
          Filtros de Contas a Pagar
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Data Inicial"
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Data Final"
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Status"
                select
                value={status}
                onChange={(e) =>
                  setStatus(
                    Array.isArray(e.target.value)
                      ? e.target.value
                      : [e.target.value]
                  )
                }
                fullWidth
                SelectProps={{ multiple: true }}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? "small" : "medium"}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="contained" type="submit">
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
}

export function ListarContasAPagar() {
  const [lista, setLista] = useState<ContaAPagar[]>([]);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { showLoader, hideLoader } = useLoader();
  // Filtros padrão: início e fim do mês atual
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const dateInicio = new Date(
    firstDay.getTime() - firstDay.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 10);
  const today = new Date(
    lastDay.getTime() - lastDay.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 10);
  const [filters, setFilters] = useState<any>({ dateStart: dateInicio, dateEnd: today });

  // Resumo do mês (estado inicial seguro)
  const [resumoMes, setResumoMes] = useState<ResumoMes>({
    pending: { sum: 0, qty: 0 },
    paid: { sum: 0, qty: 0 },
  });

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    carregarLista(filters, page, rowsPerPage);
    // eslint-disable-next-line
  }, [filters, page, rowsPerPage]);

  const normalizarResumo = (soma: SumResponse | undefined): ResumoMes => {
    const toItem = (it?: ResumoItem): ResumoItem => ({
      sum: Number(it?.sum) || 0,
      qty: Number(it?.qty) || 0,
    });

    const pending = soma?.pending ?? soma?.open;
    const paid = soma?.paid ?? soma?.closed;

    return {
      pending: toItem(pending),
      paid: toItem(paid),
      anticipated: soma?.anticipated ? toItem(soma.anticipated) : undefined,
      canceled: soma?.canceled ? toItem(soma.canceled) : undefined,
    };
  };

  const carregarLista = async (filters: any, pagina = 0, limite = 10) => {
    try {
      showLoader();
      const query = montarQuery(filters);

      const result = (await contasAPagarService.findAll(
        query,
        pagina,
        limite
      )) || {
        items: [],
        totalItems: 0,
      };
      const somaResp: SumResponse = await contasAPagarService
        .sum(query)
        .catch(() => ({} as SumResponse));

      setResumoMes(normalizarResumo(somaResp));
      setLista(result.items || []);
      setTotalCount(result.totalItems || 0);
    } catch (error) {
      setLista([]);
      setResumoMes({ pending: { sum: 0, qty: 0 }, paid: { sum: 0, qty: 0 } });
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir esta conta?")) {
      try {
        const mensagem = await contasAPagarService.deletar(id);
        toast.success(mensagem);
        hideLoader();
        carregarLista(filters, page, rowsPerPage);
      } catch (error) {
        showLoader();
        toastError(error);
      }
    }
  };

  const montarQuery = (filters: any) => {
    let query = "";
    if (filters.dateStart && filters.dateEnd) {
      query += `campo_pesquisa=due_date&tipo_pesquisa=entre&valor_pesquisa=${filters.dateStart},${filters.dateEnd}&`;
    }
    if (filters.status && filters.status.length) {
      query += `campo_pesquisa=status&tipo_pesquisa=in&valor_pesquisa=${filters.status.join(
        ","
      )}&`;
    }
    query = query.replace(/&$/, "");
    return query;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  const handleOpenModal = () => setOpen(true);

  const handleFiltrar = (newFilters: any) => {
    setFilters(newFilters);
    setPage(0);
    setOpen(false);
  };

  const formatCurrency = (n: number) =>
    Number(n).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    });

  const totalMes =
    (resumoMes?.pending?.sum || 0) +
    (resumoMes?.paid?.sum || 0) +
    (resumoMes?.anticipated?.sum || 0);

  const labelStatusConta = (status: string) => {
    switch (status) {
      case "open":
        return { label: "Aberta", color: "success" as const };
      case "paid":
        return { label: "Paga", color: "primary" as const };
      case "anticipated":
        return { label: "Antecipada", color: "primary" as const };
      case "canceled":
        return { label: "Cancelada", color: "default" as const };
      default:
        return { label: status, color: "default" as const };
    }
  };

  return (
    <BaseLayout
      title="Contas a Pagar"
      barraDeFerramentas={
        <FerramanetasDaListagem
          textoBotaoNovo="Nova Conta a Pagar"
          mostrarInputBusca={false}
          mostraBotaoFiltro={true}
          aoClicarBotaoFiltro={handleOpenModal}
          aoClicarBotaoNovo={() => navigate("/financeiro/contas-a-pagar/criar")}
        />
      }
    >
      <ModalFiltrosContasAPagar
        open={open}
        onClose={() => setOpen(false)}
        onFiltrar={handleFiltrar}
      />

      {/* Resumo do mês */}
      <Grid container display="flex" flexWrap="wrap" justifyContent={"center"} alignItems="center" spacing={2} sx={{ mb: 2 }} paddingBottom={2}>
        <Grid item xs={12} sm={6} md={4} height={"100%"} >
          <Paper sx={{ p: 2, height: "100%" }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" color="text.secondary">
                Valor total do mês
              </Typography>
              <Icon color="primary">attach_money</Icon>
            </Box>
            <Typography variant="h6" mt={1}>
              {formatCurrency(resumoMes?.pending?.sum || 0)}{" "}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} height={"100%"} >
          <Paper sx={{ p: 2, height: "100%" }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" color="text.secondary">
                Dívidas em aberto no mês
              </Typography>
              <Icon color="warning">pending_actions</Icon>
            </Box>
            <Typography variant="h6" mt={1}>
              {resumoMes?.pending?.qty || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} height={"100%"} >
          <Paper sx={{ p: 2, height: "100%" }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" color="text.secondary">
                Dívidas pagas no mês
              </Typography>
              <Icon color="success">paid</Icon>
            </Box>
            <Typography variant="h6" mt={1}>
              {resumoMes?.paid?.qty || 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box height="100%">
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />

        {lista.length > 0 ? (
          lista.map((conta) => {
            const meta = labelStatusConta(conta.status);
            return (
              <Accordion key={conta.id} sx={{ mb: 2, width: "100%", '@media (max-width:599px)': { width: '600px', overflowX: 'auto' } }}>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                  <Box display="flex" flexDirection="column" width="100%">
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      noWrap
                      title={conta.description}
                      sx={{ maxWidth: "100%" }}
                    >
                      {conta.description}
                    </Typography>

                    <Box
                      display="flex"
                      alignItems="center"
                      columnGap={2}
                      rowGap={1}
                      mt={0.5}
                      flexWrap="wrap"
                    >
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        Valor Total:{" "}
                        <strong>
                          {Number(conta.totalValue).toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                            minimumFractionDigits: 2,
                          })}
                        </strong>
                      </Typography>

                      <Chip
                        label={meta.label}
                        color={meta.color}
                        size="small"
                      />

                      {conta.anticipateAccount ? (
                        <Chip
                          label="Boleto de Antecipação"
                          color="primary"
                          size="small"
                        />
                      ) : Array.isArray(conta.installments) &&
                        conta.installments.some(
                          (p) => p.status === "anticipated"
                        ) ? (
                        <Chip label="Antecipado" color="primary" size="small" />
                      ) : null}

                      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        Pagas: <strong>{conta.paidInstallments}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        Abertas: <strong>{conta.openInstallments}</strong>
                      </Typography>

                      <Box ml="auto" display="flex" gap={1}>
                        {conta.status === "open" &&
                          !conta.anticipateAccount && (
                            <>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  navigate(
                                    `/financeiro/contas-a-pagar/detalhe/${conta.id}`
                                  )
                                }
                                title="Visualizar documento"
                              >
                                <Icon>visibility</Icon>
                              </IconButton>

                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(conta.id)}
                                title="Excluir documento"
                              >
                                <Icon>delete</Icon>
                              </IconButton>
                            </>
                          )}
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Typography variant="subtitle2" mb={1}>
                    Parcelas
                  </Typography>
                  {Array.isArray(conta.installments) &&
                    conta.installments.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Descrição</TableCell>
                          <TableCell>Valor</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Vencimento</TableCell>
                          <TableCell>Pagamento</TableCell>
                          <TableCell>Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {conta.installments.map((parcela) => (
                          <TableRow key={parcela.id}>
                            <TableCell>{parcela.description}</TableCell>
                            <TableCell>
                              {Number(parcela.value).toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                // Verifica se está atrasado (status pending e data vencimento menor que hoje)
                                const isOverdue =
                                  parcela.status === "pending" &&
                                  parcela.dueDate &&
                                  new Date(parcela.dueDate) < new Date();

                                if (isOverdue) {
                                  return (
                                    <Chip
                                      label="Em atraso"
                                      color="error"
                                      size="small"
                                    />
                                  );
                                }

                                switch (parcela.status) {
                                  case "pending":
                                    return (
                                      <Chip
                                        label="Aguardando pagamento"
                                        color="info"
                                        size="small"
                                      />
                                    );
                                  case "paid":
                                    return (
                                      <Chip
                                        label="Paga"
                                        color="success"
                                        size="small"
                                      />
                                    );
                                  case "overdue":
                                    return (
                                      <Chip
                                        label="Atrasada"
                                        color="error"
                                        size="small"
                                      />
                                    );
                                  case "anticipated":
                                    return (
                                      <Chip
                                        label="Antecipada"
                                        color="primary"
                                        size="small"
                                      />
                                    );
                                  default:
                                    return (
                                      <Chip
                                        label={parcela.status}
                                        color="default"
                                        size="small"
                                      />
                                    );
                                }
                              })()}
                            </TableCell>
                            <TableCell>
                              {parcela.dueDate
                                ? new Date(parcela.dueDate).toLocaleDateString(
                                  "pt-BR"
                                )
                                : "--"}
                            </TableCell>
                            <TableCell>
                              {parcela.paymentDate
                                ? new Date(
                                  parcela.paymentDate
                                ).toLocaleDateString("pt-BR")
                                : "--"}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Icon>payments</Icon>}
                                onClick={() =>
                                  navigate(
                                    `/financeiro/contas-a-pagar/liquidar/${parcela.id}`,
                                    {
                                      state: {
                                        parcela,
                                        conta,
                                      },
                                    }
                                  )
                                }
                                disabled={
                                  parcela.status === "paid" ||
                                  parcela.status === "anticipated"
                                }
                              >
                                Liquidar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma parcela encontrada.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <RetornoDaListagemVazia />
        )}
      </Box>
    </BaseLayout>
  );
}
